import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

import { type DeliveryFacets, type DeliveryMediaFilter } from '@/services/deliveries';

import { gallery as copy, galleryMediaLabels } from '../content';

type Props = {
  facets: DeliveryFacets | undefined;
  destination: string | null;
  media: DeliveryMediaFilter;
  onDestinationChange: (destination: string | null) => void;
  onMediaChange: (media: DeliveryMediaFilter) => void;
};

/** Which chips to draw, in order, with the one that is on. */
const mediaOrder: readonly DeliveryMediaFilter[] = ['all', 'photos', 'videos', 'testimonies'];

/**
 * Two rows of chips: where it went, and what to show.
 *
 * ## They are buttons in a list, not a tablist
 *
 * A `tablist` promises arrow-key navigation between tabs and exactly one
 * selected at a time, and these are two independent filters that combine — so
 * the role would be a lie that a screen reader then holds us to. They are
 * `aria-pressed` toggle buttons in a labelled group, which is what they are.
 *
 * MUI's `Chip` renders a `<div>` by default; `component="button"` with
 * `clickable` is what makes each one a real control that Tab reaches and the
 * space bar fires.
 *
 * ## Counts on the destination chips, not on the media ones
 *
 * "Kampala (14)" tells a visitor whether a tap is worth it. "Photos (31)" does
 * not — the number is the archive's shape rather than a reason to choose, and
 * four counted chips in a row read as a dashboard. The row's job is to narrow,
 * not to report.
 *
 * A destination with nothing published has no chip at all: the facets are
 * counted off the same list the grid reads, so a chip here cannot lead to an
 * empty grid.
 */
export function DeliveryFilterChips({
  facets,
  destination,
  media,
  onDestinationChange,
  onMediaChange,
}: Props) {
  const destinations = facets?.destinations ?? [];

  return (
    <Stack spacing={4}>
      {destinations.length > 1 && (
        <Stack
          direction="row"
          role="group"
          aria-label={copy.placeLabel}
          sx={{ flexWrap: 'wrap', gap: 2 }}
        >
          <Chip
            component="button"
            clickable
            label={copy.allPlaces}
            aria-pressed={destination === null}
            color={destination === null ? 'primary' : 'default'}
            variant={destination === null ? 'filled' : 'outlined'}
            onClick={() => onDestinationChange(null)}
          />
          {destinations.map((facet) => {
            const on = destination === facet.value;
            return (
              <Chip
                key={facet.value}
                component="button"
                clickable
                label={`${facet.value} (${facet.count})`}
                aria-pressed={on}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                // Tapping the chip that is already on clears it, which is the
                // only way back to "everywhere" on a phone without hunting
                // for the first chip again.
                onClick={() => onDestinationChange(on ? null : facet.value)}
              />
            );
          })}
        </Stack>
      )}

      <Stack
        direction="row"
        role="group"
        aria-label={copy.showLabel}
        sx={{ flexWrap: 'wrap', gap: 2 }}
      >
        {mediaOrder.map((value) => {
          const on = media === value;
          // A chip for a kind the archive has none of would be a dead end.
          if ((facets?.mediaCounts[value] ?? 1) === 0) return null;
          return (
            <Chip
              key={value}
              component="button"
              clickable
              label={galleryMediaLabels[value]}
              aria-pressed={on}
              color={on ? 'primary' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              onClick={() => onMediaChange(value)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
